<template>
  <div class="column content-box">
    <div class="empty-wrapper">
      <woot-loading-state
        v-if="isLoading"
        :message="$t('INTEGRATION_APPS.FETCHING')"
      />
    </div>

    <div v-if="!isLoading" class="applications-grid">
      <integration-item
        v-for="item in applicationsList"
        :key="item.id"
        :integration-id="item.id"
        :integration-logo="item.logo"
        :integration-icon="item.icon"
        :integration-name="item.name"
        :integration-description="item.description"
        :integration-path="item.path"
      />
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import IntegrationItem from './IntegrationItem';
import internalApplications from './applicationCatalog';
import { frontendURL } from '../../../../helper/URLHelper';
import {
  isMenuItemAccessible,
  toBCP47Locale,
} from '../../../../helper/menuItemVisibility';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';

const PARENT_ACCOUNT_USER_TYPES = ['SuperAdmin', 'UserSupport'];

export default {
  components: {
    IntegrationItem,
  },
  mixins: [globalConfigMixin],
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      integrationsUiFlags: 'integrations/getUIFlags',
      appIntegrations: 'integrations/getAppIntegrations',
      currentUser: 'getCurrentUser',
      currentRole: 'getCurrentRole',
      typeAccount: 'getCurrentTypeAccount',
      isAccountTypeParent: 'isAccountTypeParent',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
      globalConfig: 'globalConfig/get',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOpportunityPipelinesManagementAllowed:
        'accounts/isOpportunityPipelinesManagementAllowed',
      assistentUiFlags: 'assistent/getUIFlags',
      assistents: 'assistent/getAssistents',
    }),
    isLoading() {
      return (
        this.integrationsUiFlags.isFetching ||
        this.assistentUiFlags.fetchingList
      );
    },
    menuVisibilityContext() {
      return {
        currentRole: this.currentRole,
        typeAccount: this.typeAccount,
        currentUser: this.currentUser,
        isOnChatwootCloud: this.isOnChatwootCloud,
        globalConfig: this.globalConfig,
        accountId: this.accountId,
        isFeatureEnabledonAccount: this.isFeatureEnabledonAccount,
        isOpportunityPipelinesManagementAllowed: this
          .isOpportunityPipelinesManagementAllowed,
      };
    },
    apiApplications() {
      return this.appIntegrations
        .filter(item => {
          if (item.id === 'assistent') {
            return !this.assistentUiFlags.isFetching && this.assistents.length;
          }
          return true;
        })
        .map(item => ({
          id: item.id,
          source: 'api',
          logo: item.logo,
          icon: '',
          name: item.name,
          description: this.useInstallationName(
            item.description,
            this.globalConfig.installationName
          ),
          path: frontendURL(
            `accounts/${this.accountId}/settings/applications${item.action ||
              ''}`
          ),
        }));
    },
    internalApplications() {
      return internalApplications(this.accountId)
        .filter(item => isMenuItemAccessible(item, this.menuVisibilityContext))
        .map(item => ({
          id: item.id,
          source: 'internal',
          logo: item.logo || '',
          icon: item.icon,
          name: this.$t(item.nameKey),
          description: this.$t(item.descriptionKey),
          path: item.toState,
        }));
    },
    applicationsList() {
      if (this.isAccountTypeParent) {
        if (!PARENT_ACCOUNT_USER_TYPES.includes(this.currentUser.type)) {
          return [];
        }
        return this.apiApplications.filter(
          item => item.id === 'dashboard-apps'
        );
      }

      const locale = toBCP47Locale(this.$i18n.locale);
      return [
        ...this.apiApplications,
        ...this.internalApplications,
      ].sort((a, b) =>
        a.name.localeCompare(b.name, locale, { sensitivity: 'base' })
      );
    },
  },
  mounted() {
    this.$store.dispatch('integrations/get');
    this.$store.dispatch('assistent/get', { version: 'v1' });
  },
};
</script>

<style scoped lang="scss">
.empty-wrapper {
  margin: var(--space-zero) auto;
}

.applications-grid {
  display: grid;
  align-items: stretch;
  gap: var(--space-normal);
  grid-template-columns: 1fr;
  padding: var(--space-normal);

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media screen and (min-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media screen and (min-width: 1440px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
